import { AlertCircle, ShieldAlert } from "lucide-react";

import { Button } from "@/common/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/common/ui/card";
import { Skeleton } from "@/common/ui/skeleton";

import { BreakdownCards } from "./BreakdownCards";
import { EventDetailPanel } from "./EventDetailPanel";
import { EventsTable } from "./EventsTable";
import { MetricsToolbar } from "./MetricsToolbar";
import { SummaryCards } from "./SummaryCards";
import { useGuardrailMetrics } from "./use-guardrail-metrics";

export function GuardrailMetricsPage(): React.ReactElement {
  const {
    summary,
    events,
    isLoading,
    error,
    filters,
    setFilters,
    search,
    selectedEvent,
    selectEvent,
  } = useGuardrailMetrics();

  return (
    <div data-testid="guardrail-metrics-page" className="space-y-6">
      <MetricsToolbar
        filters={filters}
        onFiltersChange={setFilters}
        onSearch={search}
        isLoading={isLoading}
      />

      {error && (
        <div
          data-testid="metrics-error"
          className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive"
        >
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={search}>
            Retry
          </Button>
        </div>
      )}

      {isLoading && (
        <div data-testid="metrics-loading" className="space-y-3">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <Skeleton className="h-48 w-full" />
        </div>
      )}

      {!isLoading && !summary && !error && (
        <Card data-testid="empty-metrics-card" className="mx-auto max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <ShieldAlert className="h-8 w-8 text-primary" />
            </div>
            <CardTitle>No Metrics Found</CardTitle>
            <CardDescription>
              No guardrail metrics match the current filters. Try adjusting the time range or
              filters.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!isLoading && summary && (
        <>
          <SummaryCards summary={summary} />

          <BreakdownCards
            byPolicy={summary.by_policy}
            bySource={summary.by_source}
            byAction={summary.by_action}
          />

          {events.length > 0 && (
            <div className="flex gap-6">
              <div className="min-w-0 flex-1">
                <div data-testid="events-count" className="mb-2 text-sm text-muted-foreground">
                  {summary.intervention_count > events.length ? (
                    <>
                      Showing {events.length} of {summary.intervention_count} intervention event
                      {summary.intervention_count !== 1 ? "s" : ""}
                    </>
                  ) : (
                    <>
                      {summary.intervention_count} intervention event
                      {summary.intervention_count !== 1 ? "s" : ""}
                    </>
                  )}
                </div>
                <Card>
                  <CardContent className="p-0">
                    <EventsTable
                      events={events}
                      selectedEvent={selectedEvent}
                      onSelect={selectEvent}
                    />
                  </CardContent>
                </Card>
              </div>

              {selectedEvent && (
                <div className="w-96 shrink-0">
                  <EventDetailPanel event={selectedEvent} onClose={() => selectEvent(null)} />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
